import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from './cn';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  /** Optional footer area (typically action buttons). */
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  /**
   * Accessible name for the dialog. Used when `title` is omitted, or as a
   * fallback name when `title` is a non-string ReactNode. When `title` is a
   * string it already provides the accessible name via `aria-labelledby`.
   */
  ariaLabel?: string;
}

const SIZES: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

/** Elements considered focusable for initial focus and the Tab focus trap. */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Accessible modal dialog rendered in a portal with backdrop + Escape close.
 *
 * Accessibility: on open, focus moves into the dialog (first focusable element,
 * else the dialog container) and Tab/Shift+Tab are trapped within it; on close,
 * focus is restored to the element that was focused before the dialog opened.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  className,
  ariaLabel,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const titleId = useId();

  // Keep the latest onClose without re-running the focus effect (which would
  // otherwise re-trap focus and clobber the saved element on every render).
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return;

    // Remember what had focus so we can restore it when the dialog closes.
    previouslyFocused.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const dialog = dialogRef.current;

    const focusable = () =>
      dialog
        ? Array.from(
            dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
          )
        : [];

    // Move focus into the dialog: first focusable element, else the container.
    const initial = focusable();
    if (initial.length > 0) initial[0].focus();
    else dialog?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !dialog) return;

      const items = focusable();
      if (items.length === 0) {
        // Nothing focusable inside — keep focus pinned to the dialog.
        e.preventDefault();
        dialog.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !dialog.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last || !dialog.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      // Restore focus to whatever was focused before the dialog opened.
      const toRestore = previouslyFocused.current;
      if (toRestore && document.contains(toRestore)) toRestore.focus();
    };
  }, [open]);

  if (!open) return null;

  const hasStringTitle = typeof title === 'string' && title.trim() !== '';
  const hasNodeTitle =
    title != null && title !== '' && typeof title !== 'string';
  const hasTitle = hasStringTitle || hasNodeTitle;

  // Accessible-name resolution:
  //  • string title  → labelled by the heading (aria-labelledby).
  //  • ReactNode title → aria-label prop when given, else the rendered heading.
  //  • no title       → aria-label prop (may be undefined).
  let ariaLabelledBy: string | undefined;
  let ariaLabelValue: string | undefined;
  if (hasStringTitle) {
    ariaLabelledBy = titleId;
  } else if (hasNodeTitle) {
    if (ariaLabel) ariaLabelValue = ariaLabel;
    else ariaLabelledBy = titleId;
  } else {
    ariaLabelValue = ariaLabel;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="presentation"
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabelledBy}
        aria-label={ariaLabelValue}
        tabIndex={-1}
        className={cn(
          'relative w-full rounded-2xl border border-surface-border bg-surface-2 shadow-pop animate-slide-up outline-none',
          SIZES[size],
          className,
        )}
      >
        <div className="flex items-center justify-between border-b border-surface-border px-5 py-4">
          {hasTitle ? (
            <h2 id={titleId} className="text-sm font-semibold text-zinc-100">
              {title}
            </h2>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="rounded-lg p-1 text-zinc-400 transition-colors hover:bg-surface-3 hover:text-zinc-200"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-surface-border px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
